import { BrowserChannel } from './BrowserChannel';
import { SessionDirectory } from './SessionDirectory';
import { SessionTerminationRecordStore } from './SessionTerminationRecordStore';
import {
  SESSION_CHANNEL_NAME,
  isSessionMessage,
  sessionRecordKey
} from './sessionTermination.helpers';
import type {
  SessionLockManager,
  SessionMessage,
  SessionTerminationReason,
  SessionTerminationRecord,
  SessionTerminationResult
} from './sessionTermination.types';

// ─── Types ──────────────────────────────────────────

/** Collaborators default to the real browser ones; tests inject fakes. */
export interface SessionTerminationOptions {
  directory?: Pick<SessionDirectory, 'current'>;
  records?: Pick<SessionTerminationRecordStore, 'read' | 'write'>;
  channel?: Pick<BrowserChannel, 'publish' | 'subscribe'>;
  /*
   * Optional cross-tab lock. Production uses the browser LockManager; tests
   * inject a serial fake. Omit or leave unimplemented when the browser has
   * no locks — logout still proceeds.
   */
  locks?: SessionLockManager;
}

// ─── Helpers ────────────────────────────────────────

/*
 * Best-effort wrapper around the Web Locks API.
 *
 * Duplicate tabs share one session id. If both click logout at the same
 * moment, both can reach endOwned before either writes the closing record.
 * A lock named for that session id lets only one tab claim the close.
 *
 * Returns undefined when navigator.locks is missing (jsdom, older browsers)
 * so callers skip locking instead of throwing.
 */
function getBrowserLocks(): SessionLockManager | undefined {
  if (typeof navigator === 'undefined' || !navigator.locks) return undefined;

  return {
    request: (name, callback) => navigator.locks.request(name, () => callback())
  };
}

// ─── Class ──────────────────────────────────────────

/**
 * Makes one server close serve every tab sharing a backend session.
 *
 * Duplicated tabs share a session id, so without coordination each one would
 * call `CloseSession` for a session the first tab already ended. Exactly one
 * tab here runs the close; the rest are told it happened so they can clear
 * their own state and leave the page they are on.
 *
 * Both the close itself and the login redirect are the caller's; this class
 * only decides who runs them and tells the others.
 */
export class SessionTermination {
  private readonly directory: Pick<SessionDirectory, 'current'>;

  private readonly records: Pick<
    SessionTerminationRecordStore,
    'read' | 'write'
  >;

  private readonly channel: Pick<BrowserChannel, 'publish' | 'subscribe'>;

  /*
   * Serializes CloseSession for one session id across tabs. Undefined means
   * this environment cannot lock; end() then closes without waiting.
   */
  private readonly locks?: SessionLockManager;

  constructor(options: SessionTerminationOptions = {}) {
    this.directory = options.directory ?? new SessionDirectory();
    this.records = options.records ?? new SessionTerminationRecordStore();
    this.channel = options.channel ?? new BrowserChannel(SESSION_CHANNEL_NAME);
    this.locks = options.locks ?? getBrowserLocks();
  }

  /**
   * Ends this tab's session, running `close` only if this tab wins ownership.
   *
   * `close` is where the caller sends `CloseSession` and, for a deliberate
   * logout, saves the resume URL — both of which must happen once per session,
   * which is why a follower never runs it.
   *
   * Locking is best-effort: if locks are unavailable, or the lock request
   * itself fails before the work began, this tab closes anyway. A stuck logout
   * is worse than a rare duplicate close.
   */
  async end(
    reason: SessionTerminationReason,
    close: () => Promise<void>
  ): Promise<SessionTerminationResult> {
    const session = this.directory.current();

    if (!session) return { executed: false, state: 'session-unavailable' };

    const operation = () => this.endOwned(session.sessionId, reason, close);

    /*
     * No LockManager: skip the queue and close. A duplicate CloseSession is
     * preferred over a tab that cannot log out.
     */
    if (!this.locks) return operation();

    let operationStarted = false;
    let operationResult: SessionTerminationResult | undefined;

    try {
      /*
       * Hold the lock for this session id until the closing record exists.
       * The second tab then sees that record in endOwned and becomes a
       * follower instead of calling CloseSession again.
       */
      return await this.locks.request(
        sessionRecordKey(session.sessionId),
        async () => {
          operationStarted = true;
          operationResult = await operation();

          return operationResult;
        }
      );
    } catch {
      /*
       * Lock acquisition failed before any work: close anyway.
       * Work already ran: return that result, or uncertain if it was lost,
       * so we do not send a second CloseSession for a session that may
       * already be gone.
       */
      if (!operationStarted) return operation();

      if (operationResult) return operationResult;

      // The work began but its result was lost, so the close may well have
      // reached the server. Report it as uncertain rather than running again.
      return {
        executed: true,
        state: 'uncertain',
        record: this.readRecord(session.sessionId)
      };
    }
  }

  /**
   * Records that the server has already ended this tab's session, and tells
   * every tab sharing it.
   *
   * Unlike `end()` there is nothing to own: the session is gone, so no request
   * is sent and no lock is taken. That keeps this synchronous, which callers
   * rely on — the expiry is detected moments before session storage is cleared,
   * and once it is cleared there is no identity left to announce.
   */
  expire(): void {
    const session = this.directory.current();

    if (!session) return;

    // Another tab, or a logout still in flight, already owns this close.
    if (this.readRecord(session.sessionId)) return;

    const now = Date.now();

    this.writeRecord({
      sessionId: session.sessionId,
      reason: 'expired',
      state: 'closed',
      createdAt: now,
      updatedAt: now
    });

    this.channel.publish({
      type: 'SESSION_CLOSED',
      sessionId: session.sessionId,
      reason: 'expired'
    });
  }

  /**
   * Watches for the close of *this* tab's session.
   *
   * `onEnded` fires at most once, and only for a close that finished. The
   * caller uses it to clear local session state and send the tab to login.
   *
   * `onCloseOtherSessions` fires when another session — typically a window
   * opened through Open in New Window — asks this one to close itself. The
   * caller decides whether that request is honored (the Close Cloned Windows
   * on Logout toggle). The origin of the message never sees it.
   *
   * Covers the tab that was listening, the tab that only saw the storage echo,
   * and the tab that missed both and comes back later.
   *
   * @returns a function that stops watching.
   */
  watch(
    onEnded: (
      message: Extract<SessionMessage, { type: 'SESSION_CLOSED' }>
    ) => void,
    onCloseOtherSessions?: (
      message: Extract<SessionMessage, { type: 'CLOSE_OTHER_SESSIONS' }>
    ) => void
  ): () => void {
    const session = this.directory.current();

    if (!session) return () => undefined;

    let delivered = false;
    let closeOthersDelivered = false;

    const accept = (message: SessionMessage): void => {
      // Re-read rather than trusting the id captured at subscribe time: this
      // tab may have signed in again since.
      if (this.directory.current()?.sessionId !== session.sessionId) return;

      if (message.type === 'CLOSE_OTHER_SESSIONS') {
        if (closeOthersDelivered) return;

        // This tab originated the close; it is not "another" session.
        if (message.originSessionId === session.sessionId) return;

        closeOthersDelivered = true;
        onCloseOtherSessions?.(message);
        return;
      }

      if (delivered) return;

      if (message.type !== 'SESSION_CLOSED') return;

      if (message.sessionId !== session.sessionId) return;

      delivered = true;
      onEnded(message);
    };

    /** Catches a close that finished while this tab was not listening. */
    const reconcile = (): void => {
      if (
        delivered ||
        this.directory.current()?.sessionId !== session.sessionId
      )
        return;

      const record = this.readRecord(session.sessionId);

      // An uncertain close is not proof the session ended; leave the tab be.
      if (!record || record.state !== 'closed') return;

      accept({
        type: 'SESSION_CLOSED',
        sessionId: record.sessionId,
        reason: record.reason
      });
    };

    const unsubscribe = this.channel.subscribe(isSessionMessage, accept);

    window.addEventListener('pageshow', reconcile);
    document.addEventListener('visibilitychange', reconcile);

    // A close may already have finished before this tab started watching.
    reconcile();

    return () => {
      unsubscribe();

      window.removeEventListener('pageshow', reconcile);
      document.removeEventListener('visibilitychange', reconcile);
    };
  }

  /** Runs the close for the one tab that owns it, and announces the outcome. */
  private async endOwned(
    sessionId: string,
    reason: SessionTerminationReason,
    close: () => Promise<void>
  ): Promise<SessionTerminationResult> {
    if (this.directory.current()?.sessionId !== sessionId)
      return { executed: false, state: 'session-unavailable' };

    // Any existing record means another tab already owns this close, whatever
    // state it reached. This tab becomes a follower.
    const previous = this.readRecord(sessionId);

    if (previous) {
      return { executed: false, state: previous.state, record: previous };
    }

    const now = Date.now();

    // Claim ownership before the request goes out, so a tab that starts while
    // this one is still waiting finds the claim instead of closing again.
    const closing: SessionTerminationRecord = {
      sessionId,
      reason,
      state: 'closing',
      createdAt: now,
      updatedAt: now
    };

    this.writeRecord(closing);

    let state: 'closed' | 'uncertain';

    try {
      await close();
      state = 'closed';
    } catch {
      state = 'uncertain';
    }

    const completed: SessionTerminationRecord = {
      ...closing,
      state,
      updatedAt: Math.max(now, Date.now())
    };

    this.writeRecord(completed);

    // Only a confirmed close may push other tabs off their page.
    if (state === 'closed') {
      this.channel.publish({
        type: 'SESSION_CLOSED',
        sessionId,
        reason
      });

      /*
       * Cloned windows have a different session id, so SESSION_CLOSED does
       * not reach them. They decide locally whether to honor this, using the
       * Close Cloned Windows on Logout toggle. Expired is omitted: the server
       * ended this session, not the clones.
       */
      if (reason !== 'expired') {
        this.channel.publish({
          type: 'CLOSE_OTHER_SESSIONS',
          originSessionId: sessionId,
          reason
        });
      }
    }

    return { executed: true, state, record: completed };
  }

  /** An unreadable record is treated as absent, never as a finished close. */
  private readRecord(sessionId: string): SessionTerminationRecord | undefined {
    try {
      return this.records.read(sessionId);
    } catch {
      return undefined;
    }
  }

  /** Losing the record costs us coordination, not the logout itself. */
  private writeRecord(record: SessionTerminationRecord): void {
    try {
      this.records.write(record);
    } catch {
      // Intentionally ignored: the close must still go ahead.
    }
  }
}
